import { ReactNode, useEffect, useState } from 'react';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Fade, IconButton } from '@mui/material';
import type { KeenSliderInstance } from 'keen-slider';
import { useKeenSlider } from 'keen-slider/react';

import 'keen-slider/keen-slider.min.css';

type CardCarousel<T = unknown> = {
    list: T[];
    navButtons?: boolean;
    indicators?: boolean;
    interval?: number;
    loop?: boolean;
    renderCard: (item: T, index: number) => ReactNode;
};

/**
 * Realiza autoplay no carrossel.
 */
const createAutoplay = (delay: number) => (slider: KeenSliderInstance) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let mouseOver = false;

    const clearNextTimeout = () => {
        if (timeout) clearTimeout(timeout);
    };

    const nextTimeout = () => {
        clearNextTimeout();

        if (mouseOver) return;

        timeout = setTimeout(() => slider.next(), delay);
    };

    slider.on('created', () => {
        slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
        });

        slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
        });

        nextTimeout();
    });

    slider.on('dragStarted', clearNextTimeout);
    slider.on('animationEnded', nextTimeout);
    slider.on('updated', nextTimeout);
};

export function CardCarousel<T = unknown>({
    list = [],
    loop = true,
    navButtons = true,
    indicators = true,
    interval = 8000,
    renderCard,
}: CardCarousel<T>) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
        {
            loop,
            created: () => setLoaded(true),
            slideChanged: (slider) => setCurrentSlide(slider.track.details.rel),
        },
        interval && list.length > 1 ? [createAutoplay(interval)] : [],
    );

    /**
     * Atualiza o slider ao mudar a lista.
     * Move o slider ao início.
     */
    const updateSlider = () => {
        if (!instanceRef.current) return;

        instanceRef.current.update();

        instanceRef.current.moveToIdx(0, true, { duration: 0 });
    };

    useEffect(updateSlider, [list, instanceRef]);

    const hasNavigation = navButtons && loaded && instanceRef.current && list.length > 1;
    const hasIndicators = indicators && loaded && instanceRef.current && list.length > 1;

    if (!list.length) return null;

    return (
        <Fade in={true} timeout={800}>
            <Box sx={{ width: '100%' }}>
                <Box ref={sliderRef} className="keen-slider" sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
                    {list.map((item, idx) => (
                        <Box className="keen-slider__slide" key={idx} sx={{ display: 'flex', justifyContent: 'center' }}>
                            {renderCard(item, idx)}
                        </Box>
                    ))}

                    {hasNavigation && (
                        <>
                            <IconButton
                                onClick={() => instanceRef.current?.prev()}
                                sx={{
                                    'position': 'absolute',
                                    'top': '50%',
                                    'left': 8,
                                    'transform': 'translateY(-50%)',
                                    'zIndex': 1,
                                    'color': 'white',
                                    'backgroundColor': 'grey.800',
                                    '&:hover': { backgroundColor: 'grey.600' },
                                }}
                            >
                                <KeyboardArrowLeft />
                            </IconButton>

                            <IconButton
                                onClick={() => instanceRef.current?.next()}
                                sx={{
                                    'position': 'absolute',
                                    'top': '50%',
                                    'right': 8,
                                    'transform': 'translateY(-50%)',
                                    'zIndex': 1,
                                    'color': 'white',
                                    'backgroundColor': 'grey.800',
                                    '&:hover': { backgroundColor: 'grey.600' },
                                }}
                            >
                                <KeyboardArrowRight />
                            </IconButton>
                        </>
                    )}
                </Box>

                {hasIndicators && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                        {list.map((_, idx) => (
                            <Box
                                key={idx}
                                onClick={() => instanceRef.current?.moveToIdx(idx)}
                                sx={{
                                    'width': 10,
                                    'height': 10,
                                    'borderRadius': '50%',
                                    'cursor': 'pointer',
                                    ':hover': { backgroundColor: 'grey.600' },
                                    'transition': 'background-color 0.3s',
                                    'backgroundColor': idx === currentSlide ? 'grey.800' : 'grey.400',
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Fade>
    );
}
